import { signOut } from "@src/auth/utils";
import { ClickableDiv } from "@src/straps/utils/ClickableDiv";
import { default as classnames, default as classNames } from "classnames";
import * as React from "react";
import { Link } from "react-router-dom";
import { useDidClickOutsideElement } from "../../../../global_functions/hooks";
import { ExtendedSemanticColors, Include } from "../../../colors";
import Button from "../../buttons/Button/Button";
import Icon, { IconNames } from "../../icons/Icon/Icon";
import Skeleton from "../../skeleton/Skeleton";
import Text from "../../type/Text/Text";

/**
 * Only the fields this component renders, so the design system stays decoupled
 * from the app-level user types.
 */
export type UserHeaderUser = {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly imageURL?: string | null;
  readonly superAdmin: boolean;
};

export type UserHeaderProps = {
  readonly className?: string;
  readonly user?: UserHeaderUser | null;
  readonly settingsURL: string;
};

const UserHeader = ({ className, user, settingsURL }: UserHeaderProps) => {
  const firstInitial = (value: string | null | undefined) =>
    `${value ?? ""}`[0] ?? "";

  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const userContainerRef = useDidClickOutsideElement(setDropdownOpen);

  if (!user) {
    return (
      <div className="flex h-[70px] w-[220px] items-center justify-between px-7 py-4">
        <Skeleton className="h-5 w-28 !bg-white/25" />
        <Skeleton className="h-9 w-9 rounded-full !bg-white/25" />
      </div>
    );
  }

  let userNameColor: "primary" | "white" | "tertiary" = "white";
  if (dropdownOpen) {
    userNameColor = "primary";
  }

  let avatarBorderClass = "border-pure-white/40";
  if (dropdownOpen) {
    avatarBorderClass = "border-straps-tertiary/40";
  }

  return (
    <ClickableDiv
      data-testid="user-header-container"
      className={classnames(
        "group relative flex min-w-[220px] cursor-pointer items-center justify-between px-7 py-4",
        {
          "bg-white": dropdownOpen,
        },
        className
      )}
      ref={userContainerRef}
      onClick={() => setDropdownOpen(!dropdownOpen)}
    >
      {/* Static Part */}
      <Text
        variant="sb_t-14-500"
        as="span"
        color={userNameColor}
        className={classnames("leading-8 transition-colors", {
          "group-hover:text-pure-white/90": !dropdownOpen,
          "group-hover:text-straps-primary-hover": dropdownOpen,
        })}
      >
        {`${user.firstName ?? ""} ${user.lastName ?? ""}`}
      </Text>
      <div
        className={classnames(
          "flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border-2 bg-straps-primary shadow-md",
          avatarBorderClass
        )}
      >
        {user.imageURL ? (
          <img src={user.imageURL} alt="user-avatar" className="h-9 w-9" />
        ) : (
          <div className="text-xs text-white">
            {firstInitial(user.firstName)}
            {firstInitial(user.lastName)}
          </div>
        )}
      </div>
      {/* Floating Part */}
      <div
        data-testid="user-header-dropdown-container"
        className={classnames(
          "pointer-events-none absolute right-0 top-full w-full overflow-hidden bg-white opacity-0 shadow-lg transition-opacity",
          {
            "pointer-events-auto opacity-100": dropdownOpen,
          }
        )}
      >
        <div className="border-b border-t border-straps-tertiary border-opacity-25 px-7 py-4">
          {settingsURL && (
            <LinkButton
              data-testid="user-header-settings-button"
              onClick={() => {
                setDropdownOpen(false);
              }}
              href={settingsURL}
              icon="gear"
              color="brand"
            >
              Settings
            </LinkButton>
          )}
          <Button
            dataTestId="user-header-logout-button"
            className="w-full !justify-between !pl-5 !pr-7"
            iconSize="medium"
            iconRight
            color="primary"
            onClick={() => {
              signOut();
              setDropdownOpen(false);
            }}
            icon="circle-out"
          >
            <Text variant="sa_t-12-700" color="white">
              Logout
            </Text>
          </Button>
        </div>
        {user.superAdmin && (
          <div className="group/switch flex flex-col px-7 py-4">
            <Text
              variant="sb_t-10-500"
              color="tertiary"
              className="mb-1 group-hover/switch:text-straps-primary"
            >
              Switch To
            </Text>
            <LinkButton
              data-testid="user-header-switch-to-arcpack-button"
              href="/arcpack"
              icon="utilities"
              color="body"
            >
              <span>
                Arc<span className="text-straps-brand">Pack</span>
              </span>
            </LinkButton>
          </div>
        )}
      </div>
    </ClickableDiv>
  );
};

type Colors = Include<
  ExtendedSemanticColors,
  "primary" | "brand" | "accent-1" | "body" | "warning"
>;

const colorMap: Record<Colors, string> = {
  primary:
    "bg-straps-primary focus:outline-straps-primary-hover hover:bg-straps-primary-hover",
  brand:
    "bg-straps-brand focus:outline-straps-brand-hover hover:bg-straps-brand-hover",
  "accent-1":
    "bg-straps-accent-1 focus:outline-straps-accent-1-hover hover:bg-straps-accent-1-hover",
  // Extra note for body color - does this indicate we should have a set body-hover variant?
  body: "bg-straps-body focus:outline-straps-brand hover:bg-[#E2E5E8]", // exceptional use of non-semantic color
  warning:
    "bg-straps-warning focus:outline-straps-warning-hover hover:bg-straps-warning-hover",
};

const textStylesMap: Record<Colors, string> = {
  primary: "text-pure-white",
  brand: "text-pure-white",
  "accent-1": "text-pure-white",
  body: " text-straps-primary",
  warning: "text-straps-primary",
};

function LinkButton(props: {
  readonly href: string;
  readonly onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  readonly children: React.ReactNode;
  readonly icon: IconNames;
  readonly "data-testid": string;
  readonly color: Colors;
  readonly iconSize?: "medium" | "large";
  readonly className?: string;
}) {
  return (
    <Link
      data-testid={props["data-testid"]}
      onClick={props.onClick}
      to={props.href}
      className={classnames(
        props.className,
        "mb-2 block outline-none last:mb-0",
        colorMap[props.color]
      )}
    >
      <span
        className={classNames(
          "text flex h-[34px] w-40 cursor-pointer flex-row items-center justify-between px-5 py-1.5 text-xs font-bold uppercase transition-all hover:shadow-lg",
          textStylesMap[props.color]
        )}
      >
        {props.children}
        <Icon
          name={props.icon}
          size={props.iconSize ?? "large"}
          color={props.icon === "bractlet" ? "brand" : undefined}
        />
      </span>
    </Link>
  );
}

export default UserHeader;
